// Account-owned inbox: opening the list does not mark everything as read.
// Read/dismiss state is saved by the authenticated API, across devices.
import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Badge, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, List, ListItem, ListItemButton, Popover, Stack, Tooltip, Typography } from "@mui/material";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import DoneOutlinedIcon from "@mui/icons-material/DoneOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { AnnouncementText } from "./AnnouncementRichText";
import { announcementPlainText } from "../utils/announcementFormatting";
import { metenApi } from "../services/metenApi";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import {
  POLL_MS, announcementText, formatGreekDateTime,
} from "../utils/announcements";

export const KIND_STYLE = {
  info: { Icon: InfoOutlinedIcon, color: "info" },
  maintenance: { Icon: BuildOutlinedIcon, color: "warning" },
  important: { Icon: ErrorOutlineOutlinedIcon, color: "error" },
};

export function AnnouncementItem({ item, isNew = false }) {
  const { t, isGreek } = useLanguage();
  const style = KIND_STYLE[item.kind] || KIND_STYLE.info;
  const { title, body } = announcementText(item, isGreek);
  const { Icon } = style;
  return (
    <Stack direction="row" spacing={1.25} sx={{ py: 1.25 }} data-testid="announcement-item">
      <Icon color={style.color} fontSize="small" sx={{ mt: "2px" }} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.25 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.35 }}><AnnouncementText value={title} /></Typography>
          {isNew ? <Chip size="small" color="primary" label={t("announcements.newBadge")} sx={{ height: 18, fontSize: 11 }} /> : null}
        </Stack>
        <Typography variant="body2" sx={{ whiteSpace: "pre-line", overflowWrap: "anywhere" }}><AnnouncementText value={body} /></Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          {t(`announcements.kind.${item.kind}`)}
          {item.ends_at ? ` · ${t("announcements.until", { time: formatGreekDateTime(item.ends_at) })}` : ""}
        </Typography>
      </Box>
    </Stack>
  );
}

export default function AnnouncementsBell() {
  const { user } = useAuth() || {};
  const { t, isGreek } = useLanguage();
  const tr = (el, en) => isGreek ? el : en;
  const userId = user?.id || null;
  const [inbox, setInbox] = useState({ owner: null, items: [] });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const epoch = useRef(0);
  const requestId = useRef(0);
  const mutating = useRef(false);
  const items = inbox.owner === userId ? inbox.items : [];

  const load = useCallback(async () => {
    if (!userId || mutating.current) return;
    const scope = epoch.current;
    const request = ++requestId.current;
    try {
      const data = await metenApi.listAnnouncements();
      if (scope === epoch.current && request === requestId.current) {
        setInbox({ owner: userId, items: Array.isArray(data?.announcements) ? data.announcements : [] });
      }
    } catch {
      // Keep what we had; the bell must never get in the way of the page.
    }
  }, [userId]);

  useEffect(() => {
    epoch.current += 1;
    mutating.current = false;
    setInbox({ owner: userId, items: [] });
    setSelectedId(null);
    setAnchorEl(null);
    setBusy(false);
    setError(null);
    if (!userId) return undefined;
    load();
    const timer = window.setInterval(() => {
      if (!document.hidden) load();
    }, POLL_MS);
    const onVisible = () => {
      if (!document.hidden) load();
    };
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      epoch.current += 1;
      window.clearInterval(timer);
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [userId, load]);

  if (!userId) return null;

  const unseen = items.filter((item) => !item.read_at);
  const selected = items.find((item) => item.id === selectedId);
  const selectedText = selected ? announcementText(selected, isGreek) : null;
  const open = Boolean(anchorEl);
  const label = unseen.length
    ? `${t("announcements.title")} · ${unseen.length === 1 ? t("announcements.newOne") : t("announcements.newMany", { count: unseen.length })}`
    : t("announcements.title");

  const changeState = async (item, dismiss = false) => {
    if (mutating.current) return;
    const scope = epoch.current;
    mutating.current = true;
    requestId.current += 1; // A pending poll must not undo the user's action.
    setBusy(true);
    setError(null);
    try {
      const state = await (dismiss ? metenApi.dismissAnnouncement(item.id) : metenApi.markAnnouncementRead(item.id));
      if (scope !== epoch.current) return;
      setInbox((current) => ({ ...current, items: dismiss
        ? current.items.filter((a) => a.id !== item.id)
        : current.items.map((a) => a.id === item.id ? { ...a, read_at: state.read_at } : a) }));
      if (dismiss) setSelectedId(null);
    } catch {
      if (scope === epoch.current) setError(tr(
        "Η αλλαγή δεν αποθηκεύτηκε. Δοκιμάστε ξανά.", "The change could not be saved. Please try again."));
    } finally {
      if (scope === epoch.current) {
        mutating.current = false;
        setBusy(false);
      }
    }
  };

  return (
    <>
      <Tooltip title={label}>
        <IconButton
          color="inherit"
          onClick={(event) => { setAnchorEl(event.currentTarget); setError(null); }}
          aria-label={label}
          data-testid="announcements-bell"
          data-no-translate=""
          sx={{ mr: 1, width: 40, height: 40, backgroundColor: "rgba(255,255,255,0.25)",
            border: "1px solid rgba(255,255,255,0.6)" }}
        >
          <Badge color="error" badgeContent={unseen.length} max={99} overlap="circular">
            <NotificationsNoneOutlinedIcon fontSize="small" />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { width: 440, maxWidth: "calc(100vw - 24px)", borderRadius: 2 } } }}
      >
        <Box data-no-translate="">
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.25 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{t("announcements.title")}</Typography>
              <Typography variant="caption" color="text.secondary">
                {unseen.length ? tr(`${unseen.length} ${unseen.length === 1 ? "μη αναγνωσμένη" : "μη αναγνωσμένες"}`, `${unseen.length} unread`) : items.length ? tr("Όλες αναγνωσμένες", "All read") : tr("Καμία ειδοποίηση", "No notifications")}
              </Typography>
            </Box>
            <IconButton aria-label={tr("Κλείσιμο λίστας", "Close list")} onClick={() => setAnchorEl(null)} size="small"><CloseOutlinedIcon /></IconButton>
          </Stack>
          <Divider />
          {error && <Alert severity="error" sx={{ m: 1 }}>{error}</Alert>}
          <List disablePadding sx={{ maxHeight: "min(440px, 65vh)", overflowY: "auto" }}>
          {items.map((item) => {
            const { title } = announcementText(item, isGreek);
            const { Icon, color } = KIND_STYLE[item.kind] || KIND_STYLE.info;
            return <ListItem key={item.id} disablePadding divider sx={{ alignItems: "stretch", bgcolor: item.read_at ? "transparent" : "action.hover" }}>
              <ListItemButton onClick={() => { setSelectedId(item.id); setAnchorEl(null); setError(null); }} sx={{ alignItems: "flex-start", gap: 1.25, px: 2, py: 1.5, minWidth: 0 }}>
                <Icon color={color} fontSize="small" sx={{ mt: 0.25, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: item.read_at ? 500 : 700, overflowWrap: "anywhere", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}><AnnouncementText value={title} /></Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    {t(`announcements.kind.${item.kind}`)} · {formatGreekDateTime(item.starts_at)}
                  </Typography>
                  {!item.read_at && <Typography variant="caption" color="primary" sx={{ fontWeight: 700 }}>{tr("Μη αναγνωσμένη", "Unread")}</Typography>}
                </Box>
              </ListItemButton>
              <Stack justifyContent="center" sx={{ pr: 1 }}>
                {!item.read_at && <Tooltip title={tr("Σήμανση ως αναγνωσμένο", "Mark as read")}><span>
                  <IconButton size="small" disabled={busy} aria-label={`${tr("Σήμανση ως αναγνωσμένο", "Mark as read")}: ${announcementPlainText(title)}`} onClick={() => changeState(item)}><DoneOutlinedIcon fontSize="small" /></IconButton>
                </span></Tooltip>}
                <Tooltip title={tr("Διαγραφή από τη λίστα μου", "Delete from my list")}><span>
                  <IconButton size="small" disabled={busy} aria-label={`${tr("Διαγραφή από τη λίστα μου", "Delete from my list")}: ${announcementPlainText(title)}`} onClick={() => changeState(item, true)}><DeleteOutlineOutlinedIcon fontSize="small" /></IconButton>
                </span></Tooltip>
              </Stack>
            </ListItem>;
          })}
          </List>
          {!items.length && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              {t("announcements.empty")}
            </Typography>
          )}
        </Box>
      </Popover>
      <Dialog open={Boolean(selected)} onClose={() => setSelectedId(null)} fullWidth maxWidth="sm" aria-labelledby="announcement-dialog-title" slotProps={{ paper: { sx: { borderRadius: 2 } } }}>
        {selected && <Box data-no-translate="" sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
          <DialogTitle id="announcement-dialog-title" sx={{ pr: 6, overflowWrap: "anywhere", fontWeight: 700 }}><AnnouncementText value={selectedText.title} /></DialogTitle>
          <IconButton aria-label={tr("Κλείσιμο ειδοποίησης", "Close notification")} onClick={() => setSelectedId(null)} sx={{ position: "absolute", top: 12, right: 12 }}><CloseOutlinedIcon /></IconButton>
          <DialogContent dividers sx={{ maxHeight: "60vh" }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
              {t(`announcements.kind.${selected.kind}`)} · {formatGreekDateTime(selected.starts_at)}
              {selected.ends_at ? ` · ${t("announcements.until", { time: formatGreekDateTime(selected.ends_at) })}` : ""}
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", lineHeight: 1.7 }}><AnnouncementText value={selectedText.body} /></Typography>
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          </DialogContent>
          <DialogActions sx={{ px: 2, py: 1.5, flexWrap: "wrap", gap: 1 }}>
            <Button color="inherit" disabled={busy} startIcon={<DeleteOutlineOutlinedIcon />} onClick={() => changeState(selected, true)}>{tr("Διαγραφή από τη λίστα μου", "Delete from my list")}</Button>
            <Button variant="contained" disabled={busy || Boolean(selected.read_at)} startIcon={<DoneOutlinedIcon />} onClick={() => changeState(selected)}>
              {selected.read_at ? tr("Αναγνωσμένο", "Read") : tr("Σήμανση ως αναγνωσμένο", "Mark as read")}
            </Button>
          </DialogActions>
        </Box>}
      </Dialog>
    </>
  );
}
